import { NgClass } from '@angular/common'
import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { ThemeColors, SecondaryColors } from '@wm-front/shared/enums'

export enum BadgeColor {
  GREY = 'grey',
  DANGER = 'danger',
}

export enum CountColor {
  SKY = 'sky',
  WARNING = 'warning',
  DANGER = 'danger',
}

@Component({
  selector: 'wm-front-icon-button',
  templateUrl: './icon-button.component.html',
  styleUrls: ['./icon-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, MatIconModule, MatProgressSpinnerModule],
})
export class IconButtonComponent implements OnInit {
  /** The name of the icon to display */
  @Input() public icon?: string

  /** The name of the icon to display */
  @Input() public status:
    | ThemeColors
    | SecondaryColors
    | 'transparent'
    | 'white'
    | 'on-brand'
    | `${ThemeColors}`

  /** Whether to display the badge */
  @Input() public badge: boolean

  /** The mode of the icon button component, could be just 'button' or 'route' if displayed in a menu */
  @Input() public mode: 'button' | 'route'

  /** A count to be displated on top of the icon button when there are notifications or several items for example */
  @Input() public count?: number

  /** Disabled the icon button component */
  @Input()
  public disabled?: boolean = false

  /** The shape of the icon button component */
  @Input()
  public shape?: 'round'

  /** Whether or not the button is flat */
  @Input()
  public flat = true

  /** Whether or not the button should display a loader instead of the icon */
  @Input()
  public loading = false

  /** Badge Color */
  @Input() public badgeColor: BadgeColor | `${BadgeColor}`

  /** Badge Color */
  @Input() public countColor: CountColor | `${CountColor}`

  protected _elementRef = inject(ElementRef)

  /** Disabled click and hover */
  @HostBinding('style.pointer-events')
  public get pEvents(): string {
    if (this.disabled) {
      return 'none'
    }
    return 'auto'
  }

  /** Whether or not the component should have the class 'button' */
  @HostBinding('class.button')
  public get button(): boolean {
    return this.mode === 'button'
  }

  /** Whether or not the component should have the class 'route' */
  @HostBinding('class.route')
  public get route(): boolean {
    return this.mode === 'route'
  }

  /** Whether or not the component should have the class 'flat' */
  @HostBinding('class.flat')
  public get isFlat(): boolean {
    return this.flat
  }

  /** Whether or not the component should have the class 'disabled' */
  @HostBinding('class.disabled')
  @HostBinding('attr.disabled')
  public get isDisabled(): boolean {
    return !!this.disabled
  }

  /** Listen the click for remove focus status */
  @HostListener('click')
  public click(): void {
    this._elementRef.nativeElement.blur()
  }

  public ngOnInit(): void {
    if (this.status) {
      this._elementRef.nativeElement.classList.add(this.status)
    }

    if (this.shape) {
      this._elementRef.nativeElement.classList.add(this.shape)
    }
  }
}
